'use client';

import { fetcher } from '@insta-club/api';
import { ClockIcon } from '@insta-club/components/icons';
import { LastProgram } from '@insta-club/models';
import { useSessionStore } from '@insta-club/state';
import { getJalaaliWithAddDayReadable, secondToDay } from '@insta-club/utilities';
import useSWR from 'swr';
import ProgramAlert from './program-alert';
import WelcomeVideo from './welcome-video';

interface ProgramWrapperProps {
    children: (id: number) => React.ReactNode;
    skeleton: React.ReactNode;
}

interface CheckLastProgramIDProps {
    pageID: number;
    skeleton: React.ReactNode;
    children: (id: number) => React.ReactNode;
}

const fetchData = (url: string) => fetcher(url, 'dashboard', 'GET');

const CheckActivePage = ({ skeleton, children }: ProgramWrapperProps) => {
    const sessionStatus = useSessionStore((state) => state.status);
    const activePageId = useSessionStore((state) => state.activePageId);

    if (sessionStatus === 'loading') return skeleton;

    if (!activePageId) return <WelcomeVideo />;

    return <>{children(activePageId)}</>;
};

const CheckLastProgramID = ({ skeleton, pageID, children }: CheckLastProgramIDProps) => {
    const activePage = useSessionStore((state) => state.page?.page);
    const { data, error, isLoading } = useSWR<LastProgram>(`/programs/last/${pageID}`, fetchData);

    if (isLoading) return skeleton;

    if (error || !data) return null;

    if (!data.program_id) {
        return <ProgramAlert alertKey='program-request' link={`/profile/add-page/review?page_id=${pageID}`} />;
    }

    // if (data.status === 'expired') {
    //     return <ProgramAlert alertKey='program-expired' link={`/profile/add-page/review?page_id=${pageID}`} />;
    // }

    if (data.status === 'coming_soon') {
        return (
            <ProgramAlert
                alertKey='program-to-be-created'
                extra={
                    <div className='flex flex-col gap-4'>
                        {[
                            {
                                title: 'آماده سازی برنامه : ',
                                date: getJalaaliWithAddDayReadable(secondToDay(data.seconds_remained)),
                                des: `برنامه پیج ${activePage} در روز ${getJalaaliWithAddDayReadable(secondToDay(data.seconds_remained))} بازه زمانی 12:00 الی 14:00 آماده و در اکانت شما در دسترس خواهد بود.`
                            },
                            {
                                title: 'شروع برنامه : ',
                                date: getJalaaliWithAddDayReadable(
                                    data.start_after_days + secondToDay(data.seconds_remained)
                                ),
                                des: `فعالیت‌های برنامه پیج ${activePage} در روز ${getJalaaliWithAddDayReadable(
                                    data.start_after_days + secondToDay(data.seconds_remained)
                                )} بازه زمانی 12:00 الی 14:00 شروع خواهد شد.`
                            }
                        ].map((item, index) => (
                            <div key={index} className='rounded-xl bg-gray-100 p-6'>
                                <div className='flex flex-col gap-2'>
                                    <div className='flex flex-wrap items-center gap-2'>
                                        <ClockIcon width={18} height={18} className='shrink-0 text-gray-500' />
                                        <h5 className='text-sm font-medium text-gray-600'>{item.title}</h5>
                                        <span className='text-base font-medium text-gray-800'>{item.date}</span>
                                    </div>
                                    <span className='text-sm leading-normal font-normal text-gray-500'>{item.des}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                }
            />
        );
    }

    return <>{children(data.program_id)}</>;
};

const SubscriptionRequired: React.FC<React.PropsWithChildren> = ({ children }) => {
    const hasSubscription = useSessionStore((state) => state.page?.subscription?.active);
    const hasLastSubscription = useSessionStore((state) => state.page?.last_subscription);

    if (!hasSubscription) {
        const alertKey = hasLastSubscription ? 'last-subscription' : 'not-subscription';
        return <ProgramAlert alertKey={alertKey} />;
    }

    return <>{children}</>;
};

const ProgramWrapper = ({ skeleton, children }: ProgramWrapperProps) => {
    return (
        <CheckActivePage skeleton={skeleton}>
            {(pageID) => (
                <CheckLastProgramID skeleton={skeleton} pageID={pageID}>
                    {(programID) => <SubscriptionRequired>{children(programID)}</SubscriptionRequired>}
                </CheckLastProgramID>
            )}
        </CheckActivePage>
    );
};

export default ProgramWrapper;

